<script lang="ts" setup>
import type { Brand } from "~/services/BrandService";

const props = withDefaults(
    defineProps<{
        open: boolean;
        selectedBrand: Brand;
    }>(),
    {},
);

const emit = defineEmits<{
    (e: "update:open", v: boolean): void;
}>();

const api = useApi();
const usersList = ref([]);
const loading = ref<boolean>(false);
const loadingImport = ref<boolean>(false);
const loadingImportAndInvite = ref<boolean>(false);
const selectAll = ref<boolean>(false);
const page = ref(1);
const perPage = ref(10);
const editingEmailRows = ref<Record<string, boolean>>({});
const editedEmails = ref<Record<string, string>>({});
const errorEmails = ref<boolean[]>([]);
const inputRefs = ref<Record<string, any>>({});
const users = computed(() =>
    usersList.value.slice(
        (page.value - 1) * perPage.value,
        page.value * perPage.value,
    ),
);
const selected = computed(() =>
    usersList.value.filter((u: any) => u.is_selected),
);

const isOpen = computed({
    get: () => props.open,
    set: (v) => emit("update:open", v),
});

watch(isOpen, async (isOpened: boolean) => {
    if (isOpened) {
        loading.value = true;
        try {
            const { data } = await api.core_signal.members().search({
                brand_id: props.selectedBrand.id,
            });
            usersList.value = data.map((user) => ({
                ...user,
                is_selected: !!user.email,
                disabled: !user.email,
                title: user.title ?? user.user_generated_headline,
            }));
            editingEmailRows.value = {};
            editedEmails.value = {};
        } catch (e) {
            console.error(e);
        }
        loading.value = false;
    } else {
        usersList.value = [];
        editingEmailRows.value = {};
        editedEmails.value = {};
    }
});

const columns = [
    { key: "select", label: "Select" },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "title", label: "Title" },
];

const importUsers = async (send_invite: boolean = false) => {
    let toastTitle = "Users imported successfully!";
    if (send_invite) {
        loadingImportAndInvite.value = true;
        toastTitle = "Users imported and invited successfully!";
    } else {
        loadingImport.value = true;
    }
    try {
        await api.users.import(
            props.selectedBrand.id,
            send_invite,
            selected.value.map((u: any) => ({
                id: u.id,
                email: u.email,
            })),
        );

        useToast().add({
            title: toastTitle,
        });
        emit("update:open", false);
        await refreshNuxtData();
        loadingImport.value = false;
        loadingImportAndInvite.value = false;
    } catch (e) {
        console.error(e);
    }
};

const selectRow = (row: any) => {
    if (!row.is_selected && selectAll.value) {
        selectAll.value = false;
    }
};

const onSelectAll = (value: boolean) => {
    usersList.value.forEach((user: any) => {
        user.is_selected = value && !user.disabled;
    });
};

// Set up a ref for an input element
const setInputRef = (el: any, rowId: string) => {
    if (rowId) {
        inputRefs.value[rowId] = el;
    }
};

const startEditingEmail = (row: any) => {
    if (!row || !row.id) return;

    editedEmails.value[row.id] = row.email || "";
    editingEmailRows.value[row.id] = true;

    nextTick(() => {
        const component = inputRefs.value[row.id];
        if (component && component.$el) {
            const input = component.$el.querySelector("input");
            if (input) input.focus();
        }
    });
};

const finishEditingEmail = (row: any) => {
    if (!row || !row.id) return;

    errorEmails.value[row.id] = false;

    // Only update the email if there's a valid value
    const newEmail = editedEmails.value[row.id]?.trim();
    if (newEmail && validateEmail(newEmail)) {
        row.email = newEmail;

        row.disabled = false;
        row.is_selected = true;

        editingEmailRows.value[row.id] = false;
    } else if (newEmail) {
        errorEmails.value[row.id] = true;
        row.disabled = true;
        row.is_selected = false;
    }
};

const validateEmail = (email: string) => {
    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email.toLowerCase());
};
</script>

<template>
    <UModal
        v-model="isOpen"
        :ui="{
            width: 'w-full sm:max-w-4xl',
        }"
    >
        <div class="p-8 w-full overscroll-auto overflow-auto">
            <div>
                <div class="flex flex-col gap-4">
                    <div class="flex items-center gap-4">
                        <UAvatar
                            :alt="selectedBrand.name"
                            :src="selectedBrand.avatar"
                            size="lg"
                        />
                        <span
                            class="flex flex-col gap-0 text-black text-lg font-medium"
                        >
                            <span>{{ selectedBrand.name }}</span>
                        </span>
                    </div>
                    <div class="sm:flex-auto">
                        <h1
                            class="text-base font-semibold leading-6 text-gray-900"
                        >
                            Import users from brand
                        </h1>
                        <p class="mt-2 text-sm text-gray-700">
                            You can select the users you want to import from
                            your company who are found on LinkedIn.
                        </p>
                    </div>
                </div>
                <UITable
                    :columns="columns"
                    :rows="users"
                    class="bg-hd-neutral-50 rounded-lg col-span-full w-full mt-6"
                    :loading="loading"
                    :ui="{
                        wrapper: 'overflow-auto',
                    }"
                >
                    <template #select-header>
                        <UCheckbox
                            ref="refCheckboxAll"
                            v-model="selectAll"
                            @change="onSelectAll"
                        />
                    </template>
                    <template #select-data="{ row }">
                        <UCheckbox
                            v-model="row.is_selected"
                            :disabled="row.disabled"
                            @change="selectRow(row)"
                        />
                    </template>
                    <template #empty>
                        <div
                            class="w-full flex items-center justify-center py-12"
                        >
                            No users found in this brand
                        </div>
                    </template>
                    <template #name-data="{ row }">
                        <div class="flex items-center gap-4">
                            <UAvatar
                                :alt="row.name"
                                :src="row.logo_url"
                                size="md"
                            />
                            <span
                                class="flex flex-col gap-0 text-black font-medium"
                            >
                                <span>{{ row.name }}</span>
                            </span>
                        </div>
                    </template>
                    <template #email-data="{ row }">
                        <span
                            v-if="row.email && !editingEmailRows[row.id]"
                            class="font-medium text-sm text-gray-600 flex items-center gap-2"
                        >
                            {{ row.email }}
                            <UButton
                                variant="ghost"
                                size="xs"
                                icon="i-heroicons-pencil-square"
                                @click="startEditingEmail(row)"
                            />
                        </span>
                        <div v-else class="flex gap-2">
                            <UInput
                                :ref="(el) => setInputRef(el, row.id)"
                                v-model="editedEmails[row.id]"
                                class="bg-white"
                                size="sm"
                                :color="errorEmails[row.id] ? 'red' : 'white'"
                                placeholder="Email"
                                @blur="finishEditingEmail(row)"
                            />
                        </div>
                    </template>
                </UITable>
                <div
                    class="flex py-3.5 border-t border-gray-200 dark:border-gray-700"
                >
                    <UPagination
                        v-model="page"
                        class="flex-1"
                        :page-count="perPage"
                        :total="usersList.length"
                    />
                    <div class="flex gap-4">
                        <UButton
                            class="w-full sm:w-auto"
                            color="white"
                            :disabled="loadingImport || loadingImportAndInvite"
                            @click="emit('update:open', false)"
                        >
                            Cancel
                        </UButton>
                        <UButton
                            :disabled="
                                loading ||
                                loadingImport ||
                                loadingImportAndInvite ||
                                !selected.length
                            "
                            :loading="loadingImport"
                            @click="importUsers()"
                        >
                            Import only
                        </UButton>
                        <UButton
                            :disabled="
                                loading ||
                                loadingImportAndInvite ||
                                loadingImport ||
                                !selected.length
                            "
                            :loading="loadingImportAndInvite"
                            @click="importUsers(true)"
                        >
                            Import & Send Invite
                        </UButton>
                    </div>
                </div>
            </div>
        </div>
    </UModal>
</template>
